import React, { FC, useMemo } from 'react'
import { NotionSelect } from '@/apps/latest/components/portal/notion'
import { usePharmacyListQuery } from '@/features/internal/metrics'
import { usePharmacistsQuery } from '@/features/auth'

// 概況タブの集計対象。店舗/全社/薬剤師の3軸を1つのセレクタに統合する
// (旧: 利用統計=PharmacySelector / 指導分析=薬剤師NotionSelect の2画面分立を解消)。
// 値のエンコード: 'all' = 全社 / 'ph:<id>' = 店舗 / 'pt:<id>' = 薬剤師
export type MetricsTarget = string

export const encodePharmacyTarget = (id: string): MetricsTarget => `ph:${id}`

interface TargetSelectorProps {
  value: MetricsTarget
  onChange: (value: MetricsTarget) => void
}

export const TargetSelector: FC<TargetSelectorProps> = ({
  value,
  onChange,
}) => {
  const { pharmacyData } = usePharmacyListQuery()
  const { pharmacists } = usePharmacistsQuery()

  const options = useMemo(() => {
    const result: { value: string; label: string }[] = []
    const hasHq = pharmacyData?.has_headquarters_permission ?? false
    if (hasHq) {
      result.push({ value: 'all', label: '全社' })
    }
    // 店舗軸の切り替えは本部権限のみ (旧・利用統計のPharmacySelectorがdisabledで
    // 担保していた制約)。本部権限がない場合は自店舗のみを選択肢に残す。
    // 薬剤師軸は旧・指導分析が全ユーザーに開放していたため、そのまま踏襲する
    const pharmacies = pharmacyData?.pharmacies ?? []
    for (const pharmacy of hasHq ? pharmacies : pharmacies.slice(0, 1)) {
      result.push({ value: `ph:${pharmacy.id}`, label: pharmacy.name })
    }
    for (const pharmacist of pharmacists?.pharmacists ?? []) {
      if (pharmacist.display_name && pharmacist.display_name.trim() !== '') {
        result.push({
          value: `pt:${pharmacist.id}`,
          label: `薬剤師: ${pharmacist.display_name}`,
        })
      }
    }
    return result
  }, [pharmacyData, pharmacists])

  return (
    <NotionSelect<string>
      label="対象"
      value={value}
      onChange={onChange}
      minWidth={200}
      options={options}
    />
  )
}
